import { useMemo } from 'react';
import { create } from 'zustand';
import type { CalendarEvent, CalendarFeed, Category, Member, Settings, ShoppingItem, ShoppingRule, WeatherStatus } from '@frisk/shared';
import { civilDateOf, classifyWaste, foldGerman, type FeedRole, type WasteKind } from '@frisk/shared';
import { ApiError, api, subscribe, type ItemWithExpiry } from './api';
import { goTo } from './navigation';


/**
 * Where the scan screen is in the recognise-a-barcode cycle. Unlike the
 * prototype's timer-driven fake, every transition here is caused by a real
 * event: a scanner keystroke, or a lookup resolving.
 */
export type ScanStage = 'waiting' | 'looking-up' | 'known' | 'unknown' | 'offline';

export interface Draft {
  barcode: string | null;
  name: string;
  imageUrl: string | null;
  /** Civil date or null for "kein Datum". */
  bestBefore: string | null;
  qty: number;
  /** Where the name came from, shown on the confirm screen. */
  origin: 'database' | 'manual';
  /** Raw transcript, kept so the confirm screen can show what was heard. */
  transcript: string | null;
}

export interface PromptRequest {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  /** 'numeric' swaps QWERTZ for a digit pad; 'url' adds the URL punctuation. */
  mode?: 'text' | 'numeric' | 'url' | 'password';
}

/**
 * Remembers that this device had a session, purely so an offline cold start
 * can render the cached fridge instead of a login form it cannot submit.
 *
 * This is a UI hint, not a credential: every request is still authorised by the
 * httpOnly cookie server-side, so a forged flag buys nothing but an empty app.
 */
const AUTHED_KEY = 'frisk.hadSession';

const rememberSession = (had: boolean) => {
  try {
    if (had) localStorage.setItem(AUTHED_KEY, '1');
    else localStorage.removeItem(AUTHED_KEY);
  } catch {
    /* private mode */
  }
};

const hadSession = () => {
  try {
    return localStorage.getItem(AUTHED_KEY) === '1';
  } catch {
    return false;
  }
};

const BATCH_KEY = 'frisk.batchIds';

/**
 * The round is an explicit list of item ids, not "everything added since a
 * timestamp". Deriving it from time swept up anything else that reached the
 * database meanwhile — a phone adding to the fridge, or a reseed — and showed
 * it as part of what you just put away.
 */
function readBatch(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(BATCH_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeBatch(ids: string[]): void {
  localStorage.setItem(BATCH_KEY, JSON.stringify(ids));
}

interface State {
  ready: boolean;
  authed: boolean;
  view: 'tiles' | 'list';

  items: ItemWithExpiry[];
  shopping: ShoppingItem[];
  members: Member[];
  events: CalendarEvent[];
  categories: Category[];
  settings: Settings;
  feeds: CalendarFeed[];
  /** Bumped when another panel edits the scribble board. */
  notesVersion: number;
  online: boolean;
  /** Bumped when a phone claims a pairing or puts something away. */
  scanVersion: number;
  /** When household data last came from the network, for the offline banner. */
  lastSyncAt: number | null;
  setOnline: (online: boolean) => void;
  /** Remembered so an SSE 'events' push can re-fetch the same window. */
  eventRange: { from: string; to: string } | null;

  detailId: string | null;
  toast: string | null;

  scanStage: ScanStage;
  scanBarcode: string | null;
  scanMessage: string | null;
  sttAvailable: boolean;

  draft: Draft | null;
  /** Ids put away in the current round; survives a reload. */
  batchIds: string[];

  prompt: PromptRequest | null;

  setView: (view: 'tiles' | 'list') => void;
  openDetail: (id: string | null) => void;
  flash: (message: string) => void;

  needsSetup: boolean;
  username: string;
  login: (username: string, password: string) => Promise<string | null>;
  register: (username: string, password: string, setupToken?: string) => Promise<string | null>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  weather: WeatherStatus | null;
  /**
   * The paired phone, once it has claimed. Held in the store rather than the
   * pairing modal because the modal closes the moment pairing succeeds — its
   * job was the QR code — and the connection outlives it.
   */
  phoneLink: { token: string; itemCount: number } | null;
  setPhoneLink: (link: { token: string; itemCount: number } | null) => void;
  unpairPhone: () => Promise<void>;
  rules: ShoppingRule[];
  setRule: (label: string, minQty: number, barcode?: string | null) => Promise<void>;
  updateRule: (id: string, minQty: number) => Promise<void>;
  clearRule: (id: string) => Promise<void>;
  refresh: (what?: 'items' | 'shopping' | 'members' | 'events' | 'settings' | 'weather' | 'rules') => Promise<void>;
  patchSettings: (patch: Partial<Settings>) => Promise<void>;
  patchCategory: (id: string, defaultShelfDays: number) => Promise<void>;
  changePassword: () => void;
  addFeed: (role?: FeedRole) => void;
  setFeedRole: (id: string, role: FeedRole) => Promise<void>;
  removeFeed: (id: string) => Promise<void>;
  syncFeed: (id: string) => Promise<void>;
  setFeedMember: (id: string, memberId: string | null) => Promise<void>;
  addMember: () => void;
  renameMember: (id: string, current: string) => void;
  removeMember: (id: string) => Promise<void>;
  loadEvents: (from: string, to: string) => Promise<void>;

  consume: (id: string) => Promise<void>;
  setBestBefore: (id: string, bestBefore: string | null) => Promise<void>;
  setQty: (id: string, qty: number) => Promise<void>;
  discard: (id: string) => Promise<void>;
  toShoppingList: (name: string) => Promise<void>;

  startScan: () => void;
  onBarcode: (barcode: string) => Promise<void>;
  nameUnknown: () => void;
  createItemManually: (name: string, bestBefore: string, qty: number) => Promise<void>;
  patchDraft: (patch: Partial<Draft>) => void;
  discardDraft: () => void;
  saveDraft: (then: 'more' | 'done') => Promise<void>;
  rememberInBatch: (id: string) => void;
  endBatch: () => void;

  openPrompt: (request: PromptRequest) => void;
  closePrompt: () => void;
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
let unsubscribe: (() => void) | undefined;

export const useStore = create<State>((set, get) => ({
  ready: false,
  authed: false,
  needsSetup: false,
  username: '',
  view: 'tiles',
  items: [],
  shopping: [],
  members: [],
  events: [],
  eventRange: null,
  categories: [],
  feeds: [],
  notesVersion: 0,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  scanVersion: 0,
  lastSyncAt: null,
  settings: {
    setupComplete: true, // assume set up until the server says otherwise
    timeZone: 'Europe/Berlin',
    calendarSync: false,
    voiceCapture: true,
    childLock: false,
    nightDim: true,
  },
  detailId: null,
  weather: null,
  rules: [],
  phoneLink: null,
  toast: null,

  scanStage: 'waiting',
  scanBarcode: null,
  scanMessage: null,
  sttAvailable: false,

  draft: null,
  batchIds: readBatch(),

  prompt: null,

  setOnline: (online) => set({ online }),

  setView: (view) => set({ view }),
  openDetail: (detailId) => set({ detailId }),

  flash: (message) => {
    clearTimeout(toastTimer);
    set({ toast: message });
    toastTimer = setTimeout(() => set({ toast: null }), 2200);
  },

  login: async (username, password) => {
    try {
      const me = await api.login({ username, password });
      rememberSession(true);
      set({ authed: true, username: me.username });
      await get().bootstrap();
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Anmeldung fehlgeschlagen';
    }
  },

  register: async (username, password, setupToken) => {
    try {
      const me = await api.register({ username, password, setupToken });
      rememberSession(true);
      set({ authed: true, needsSetup: false, username: me.username });
      await get().bootstrap();
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Konto konnte nicht angelegt werden';
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } finally {
      rememberSession(false);
      // A full reload is the simplest way to drop every cached slice of
      // household state along with the session.
      window.location.reload();
    }
  },

  bootstrap: async () => {
    // Only a real 401 means "not logged in". A network blip must NOT drop the
    // panel back to the code pad — it runs unattended on a wall, and a VPS
    // restart should heal by itself rather than wait for someone to retype.
    for (let attempt = 0; ; attempt++) {
      try {
        const me = await api.me();
        set({ username: me.username });
        rememberSession(true);
        break;
      } catch (err) {
        // A real 401 is the only thing that means "not logged in".
        if (err instanceof ApiError && err.status === 401) {
          const state = await api.authState().catch(() => null);
          rememberSession(false);
          set({ ready: true, authed: false, needsSetup: state?.needsSetup ?? false });
          return;
        }
        // Network failure. If this device had a session, carry on into the app
        // and let the service worker serve the cached household — showing a
        // login form that cannot reach the server would be worse than useless.
        if (hadSession()) break;
        set({ ready: true, authed: get().authed });
        await new Promise((r) => setTimeout(r, Math.min(30_000, 2 ** attempt * 1000)));
      }
    }

    // Offline, some of these resolve from the service-worker cache and some
    // reject outright (auth and stream are never cached). Keep whatever
    // arrives rather than failing the whole boot.
    const [items, shopping, members, settings, categories, feeds, weather, rules] = await Promise.all([
      api.listItems().catch(() => get().items),
      api.listShopping().catch(() => get().shopping),
      api.listMembers().catch(() => get().members),
      api.getSettings().catch(() => get().settings),
      api.listCategories().catch(() => get().categories),
      api.listFeeds().catch(() => get().feeds),
      api.weather().catch(() => get().weather),
      api.listRules().catch(() => get().rules),
    ]);
    set({
      items, shopping, members, settings, categories, feeds, weather, rules,
      authed: true, ready: true, lastSyncAt: Date.now(),
    });

    // Greys out the mic rather than letting the user record into a void.
    void api
      .sttHealth()
      .then((health) => set({ sttAvailable: health.available }))
      .catch(() => set({ sttAvailable: false }));

    unsubscribe?.();
    unsubscribe = subscribe(
      (name, payload) => {
        // The notes board owns its own state — it must not be clobbered
        // mid-stroke, so it just gets told that something changed.
        if (name === 'notes') {
          set({ notesVersion: get().notesVersion + 1 });
          return;
        }
        if (name === 'scan') {
          const scan = payload as { itemId?: string; name?: string; qty?: number } | undefined;
          if (scan?.itemId && scan.name) {
            // The phone put something away: fold it into the same batch the
            // panel's own scanner builds, and say so out loud.
            get().rememberInBatch(scan.itemId);
            get().flash(
              scan.qty && scan.qty > 1
                ? `${scan.qty}× ${scan.name} eingeräumt`
                : `${scan.name} eingeräumt`,
            );
          }
          const link = get().phoneLink;
          if (link) set({ phoneLink: { ...link, itemCount: link.itemCount + 1 } });
          set({ scanVersion: get().scanVersion + 1 });
          return;
        }
        void get().refresh(name as 'items' | 'shopping' | 'members' | 'events' | 'settings' | 'weather' | 'rules');
      },
      // Reconnected after a drop: pull everything, since the events that
      // happened meanwhile are gone.
      () => void get().refresh(),
    );
  },

  refresh: async (what) => {
    if (!what || what === 'items') set({ items: await api.listItems() });
    if (!what || what === 'shopping') set({ shopping: await api.listShopping() });
    if (!what || what === 'members') set({ members: await api.listMembers() });
    if (what === 'events') {
      const range = get().eventRange;
      if (range) set({ events: await api.listEvents(range.from, range.to) });
    }
    if (!what || what === 'rules') {
      set({ rules: await api.listRules().catch(() => get().rules) });
    }
    if (!what || what === 'weather') {
      // Never fatal: a missing forecast must not take the whole panel down
      // with it, so the widget just renders its "nicht eingerichtet" state.
      set({ weather: await api.weather().catch(() => null) });
    }
    if (what === 'settings') {
      const [settings, categories, feeds] = await Promise.all([
        api.getSettings(),
        api.listCategories(),
        api.listFeeds(),
      ]);
      set({ settings, categories, feeds });
    }
  },

  patchSettings: async (patch) => {
    // Optimistic: a toggle that lags behind the finger feels broken.
    set({ settings: { ...get().settings, ...patch } });
    try {
      set({ settings: await api.patchSettings(patch) });
    } catch {
      await get().refresh('settings');
      get().flash('Einstellung konnte nicht gespeichert werden');
    }
  },

  patchCategory: async (id, defaultShelfDays) => {
    set({
      categories: get().categories.map((c) => (c.id === id ? { ...c, defaultShelfDays } : c)),
    });
    try {
      await api.patchCategory(id, defaultShelfDays);
    } catch {
      await get().refresh('settings');
    }
  },

  /**
   * Two prompts: a name, then the URL. Typing a 150-character secret iCal link
   * on an on-screen keyboard is grim, so POST /api/calendar/feeds exists for
   * anyone who would rather paste it from a phone or a script.
   */
  /** Two prompts rather than a form: current password, then the new one. */
  changePassword: () => {
    get().openPrompt({
      label: 'AKTUELLES PASSWORT',
      value: '',
      mode: 'password',
      onCommit: (current) => {
        set({ prompt: null });
        if (!current) return;
        setTimeout(() => {
          get().openPrompt({
            label: 'NEUES PASSWORT (MIND. 10 ZEICHEN)',
            value: '',
            mode: 'password',
            onCommit: async (next) => {
              set({ prompt: null });
              if (!next) return;
              try {
                await api.changePassword(current, next);
                get().flash('Passwort geändert — andere Geräte abgemeldet');
              } catch (err) {
                get().flash(err instanceof ApiError ? err.message : 'Passwort konnte nicht geändert werden');
              }
            },
          });
        }, 60);
      },
    });
  },

  addFeed: (role = 'family') => {
    get().openPrompt({
      label: role === 'waste' ? 'MÜLLKALENDER — NAME' : 'KALENDER — NAME',
      value: '',
      onCommit: (label) => {
        set({ prompt: null });
        if (!label) return;
        setTimeout(() => {
          get().openPrompt({
            label: 'ICAL-ADRESSE (GEHEIMER LINK)',
            value: 'https://',
            mode: 'url',
            onCommit: async (url) => {
              set({ prompt: null });
              if (!url || url === 'https://') return;
              try {
                const created = await api.createFeed({ label, url, role, memberId: null });
                await get().refresh('settings');
                await get().refresh('events');
                get().flash(
                  created.sync.error
                    ? `${label}: ${created.sync.error}`
                    : `${label}: ${created.sync.count} Termine geladen`,
                );
              } catch (err) {
                get().flash(err instanceof ApiError ? err.message : 'Kalender konnte nicht angelegt werden');
              }
            },
          });
        }, 60);
      },
    });
  },

  /** Re-tagging an existing subscription, for a bin calendar added as a normal one. */
  setFeedRole: async (id, role) => {
    await api.patchFeed(id, { role });
    await Promise.all([get().refresh('settings'), get().refresh('events')]);
  },

  /** "Immer vorrätig" — creating or updating the rule for a product. */
  setPhoneLink: (phoneLink) => set({ phoneLink }),

  unpairPhone: async () => {
    const link = get().phoneLink;
    set({ phoneLink: null });
    if (link) await api.revokeScanLink(link.token).catch(() => {});
  },

  setRule: async (label, minQty, barcode = null) => {
    await api.createRule({ label, minQty, barcode });
    await Promise.all([get().refresh('rules'), get().refresh('shopping')]);
  },

  updateRule: async (id, minQty) => {
    await api.patchRule(id, { minQty });
    await Promise.all([get().refresh('rules'), get().refresh('shopping')]);
  },

  clearRule: async (id) => {
    await api.removeRule(id);
    await get().refresh('rules');
  },

  removeFeed: async (id) => {
    await api.removeFeed(id);
    await Promise.all([get().refresh('settings'), get().refresh('events')]);
  },

  syncFeed: async (id) => {
    const result = await api.syncFeed(id);
    await Promise.all([get().refresh('settings'), get().refresh('events')]);
    get().flash(result.error ?? `${result.count} Termine geladen`);
  },

  setFeedMember: async (id, memberId) => {
    await api.patchFeed(id, { memberId });
    await Promise.all([get().refresh('settings'), get().refresh('events')]);
  },

  addMember: () => {
    get().openPrompt({
      label: 'MITGLIED HINZUFÜGEN',
      value: '',
      onCommit: async (name) => {
        set({ prompt: null });
        if (!name) return;
        await api.createMember(name);
        await get().refresh('members');
        get().flash(`${name} hinzugefügt`);
      },
    });
  },

  renameMember: (id, current) => {
    get().openPrompt({
      label: 'MITGLIED UMBENENNEN',
      value: current,
      onCommit: async (name) => {
        set({ prompt: null });
        if (!name || name === current) return;
        await api.renameMember(id, name);
        await get().refresh('members');
      },
    });
  },

  removeMember: async (id) => {
    await api.removeMember(id);
    await Promise.all([get().refresh('members'), get().refresh('events')]);
  },

  loadEvents: async (from, to) => {
    set({ eventRange: { from, to } });
    set({ events: await api.listEvents(from, to) });
  },


  /** Correct the count of one batch by hand. */
  setQty: async (id, qty) => {
    await api.patchItem(id, { qty });
    await get().refresh('items');
  },

  /**
   * Correct a best-before after the fact.
   *
   * Per batch, never per product: a group holds several dates and "the" date
   * of a group is not a thing. The server re-derives the shelf-life bar and
   * folds the batch into an existing one if the new date collides.
   */
  setBestBefore: async (id, bestBefore) => {
    const item = get().items.find((i) => i.id === id);
    await api.patchItem(id, { bestBefore });
    await get().refresh('items');
    // The edited row can disappear into another batch, which would strand the
    // sheet on a dead id.
    if (!get().items.some((i) => i.id === id)) {
      const key = item ? (item.barcode ?? foldGerman(item.name)) : '';
      const sibling = get().items.find((i) => (i.barcode ?? foldGerman(i.name)) === key);
      set({ detailId: sibling?.id ?? null });
    }
  },

  /**
   * First expired, first out.
   *
   * Consuming a product takes from the soonest-expiring batch. Eating the
   * fresh jar and leaving the old one behind a tile that says everything is
   * fine would be worse than not grouping at all.
   */
  consume: async (id) => {
    const item = get().items.find((i) => i.id === id);
    await api.consumeItem(id);
    await get().refresh('items');
    // Keep the sheet open while a group still has batches left, so consuming
    // two of three does not slam it shut halfway through.
    const remaining = get().items.filter(
      (i) => (i.barcode ?? foldGerman(i.name)) === (item ? (item.barcode ?? foldGerman(item.name)) : ''),
    );
    set({ detailId: remaining[0]?.id ?? null });
    if (item) get().flash(`${item.name} verbraucht`);
  },

  discard: async (id) => {
    await api.removeItem(id);
    await get().refresh('items');
    set({ detailId: null });
  },

  toShoppingList: async (name) => {
    await api.addShopping(name);
    await get().refresh('shopping');
    set({ detailId: null });
    get().flash(`${name} → Einkaufsliste`);
  },

  // ------------------------------------------------------------ scanning

  startScan: () => {
    set({
      scanStage: 'waiting',
      scanBarcode: null,
      scanMessage: null,
      draft: null,
      detailId: null,
    });
    goTo('scan');
  },

  onBarcode: async (barcode) => {
    if (get().scanStage === 'looking-up') return;
    set({ scanStage: 'looking-up', scanBarcode: barcode, scanMessage: null });

    try {
      const product = await api.lookupBarcode(barcode);
      set({
        scanStage: 'known',
        draft: {
          barcode,
          name: product.name,
          imageUrl: product.imageUrl,
          bestBefore: null,
          qty: 1,
          origin: 'database',
          transcript: null,
        },
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Not in any database — the user names it once and we remember it.
        set({ scanStage: 'unknown', draft: null });
        return;
      }
      set({
        scanStage: 'offline',
        scanMessage:
          err instanceof ApiError && err.status === 503
            ? 'Produktdatenbank nicht erreichbar'
            : 'Suche fehlgeschlagen',
      });
    }
  },

  nameUnknown: () => {
    const barcode = get().scanBarcode;
    get().openPrompt({
      label: 'ARTIKEL BENENNEN',
      value: '',
      onCommit: (name) => {
        set({ prompt: null });
        if (!name) return;
        // Teach the barcode in the background; the flow must not wait on it.
        if (barcode) {
          void api
            .teachProduct(barcode, name)
            .then(() => get().flash('Barcode gemerkt'))
            .catch(() => get().flash('Barcode konnte nicht gemerkt werden'));
        }
        set({
          draft: {
            barcode,
            name,
            imageUrl: null,
            bestBefore: null,
            qty: 1,
            origin: 'manual',
            transcript: null,
          },
        });
        goTo('confirm');
      },
    });
  },

  /**
   * Commits straight to the database — no confirm step. There is nothing to
   * check: the user typed the name and set the date themselves, which is
   * exactly what the confirm screen exists to verify for scanned goods.
   */
  createItemManually: async (name, bestBefore, qty) => {
    const created = await api.createItem({ name, bestBefore, qty });
    get().rememberInBatch(created.id);
    await get().refresh('items');
    get().flash(`${name} eingeräumt`);
  },

  patchDraft: (patch) => {
    const draft = get().draft;
    if (draft) set({ draft: { ...draft, ...patch } });
  },

  discardDraft: () => {
    set({ draft: null, scanStage: 'waiting', scanBarcode: null, scanMessage: null });
    goTo('scan');
  },

  saveDraft: async (then) => {
    const draft = get().draft;
    if (!draft) return;

    const created = await api.createItem({
      name: draft.name,
      bestBefore: draft.bestBefore,
      qty: draft.qty,
      barcode: draft.barcode,
      imageUrl: draft.imageUrl,
    });
    get().rememberInBatch(created.id);
    await get().refresh('items');
    get().flash(`${draft.name} eingeräumt`);

    if (then === 'more') {
      set({ draft: null, scanStage: 'waiting', scanBarcode: null, scanMessage: null });
      goTo('scan');
    } else {
      get().endBatch();
      set({ draft: null });
      goTo('inhalt');
    }
  },

  rememberInBatch: (id) => {
    const ids = [...get().batchIds, id];
    writeBatch(ids);
    set({ batchIds: ids });
  },

  endBatch: () => {
    writeBatch([]);
    set({ batchIds: [] });
  },

  openPrompt: (prompt) => set({ prompt }),
  closePrompt: () => set({ prompt: null }),
}));

/**
 * Items added since this putting-away round began — the "DIESE RUNDE" strip.
 *
 * Must be a memoised hook, not a plain zustand selector: `.filter()` allocates
 * a new array on every call, and zustand compares snapshots with Object.is, so
 * a selector like this re-renders in an infinite loop.
 */
export function useBatch(): ItemWithExpiry[] {
  const items = useStore((s) => s.items);
  const batchIds = useStore((s) => s.batchIds);
  return useMemo(() => {
    const byId = new Map(items.map((item) => [item.id, item]));
    // Keep the order they were put away in, and drop ids that no longer exist.
    return batchIds.map((id) => byId.get(id)).filter((item): item is ItemWithExpiry => Boolean(item));
  }, [items, batchIds]);
}

export interface WasteDay {
  date: string;
  kinds: WasteKind[];
}

/**
 * Bin collections, grouped by the day they fall on.
 *
 * Grouped rather than listed because Restmüll and Papier on the same Tuesday
 * is one trip to the kerb, not two events. Memoised for the same reason as
 * useBatch — a selector allocating a fresh array re-renders forever.
 */
export function useWasteCollections(): WasteDay[] {
  const events = useStore((s) => s.events);
  const feeds = useStore((s) => s.feeds);

  return useMemo(() => {
    const wasteFeeds = new Set(feeds.filter((f) => f.role === 'waste').map((f) => f.id));
    if (wasteFeeds.size === 0) return [];

    const byDate = new Map<string, WasteKind[]>();
    for (const event of events) {
      if (!event.feedId || !wasteFeeds.has(event.feedId)) continue;
      const date = civilDateOf(event.startsAt);
      const kinds = byDate.get(date) ?? [];
      const kind = classifyWaste(event.title);
      // One authority can list the same bin twice on a day; the kerb only
      // needs telling once.
      if (!kinds.some((k) => k.type === kind.type)) kinds.push(kind);
      byDate.set(date, kinds);
    }

    return [...byDate.entries()]
      .map(([date, kinds]) => ({ date, kinds }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [events, feeds]);
}

/** Ids of feeds carrying bin collections, for screens that must draw them apart. */
export function useWasteFeedIds(): Set<string> {
  const feeds = useStore((s) => s.feeds);
  return useMemo(
    () => new Set(feeds.filter((f) => f.role === 'waste').map((f) => f.id)),
    [feeds],
  );
}

/**
 * The rule covering a product name, if any.
 *
 * Matching mirrors the server's: the rule key folded, contained in the folded
 * product name — so a rule for "Milch" owns "Vollmilch 1L".
 */
export function useRuleFor(name: string | undefined): ShoppingRule | null {
  const rules = useStore((s) => s.rules);
  return useMemo(() => {
    if (!name) return null;
    const folded = foldGerman(name);
    return rules.find((rule) => folded.includes(rule.matchKey)) ?? null;
  }, [rules, name]);
}

export interface ItemGroup {
  /** Barcode when there is one, folded name otherwise. */
  key: string;
  name: string;
  imageUrl: string | null;
  /** Soonest best-before first; undated batches last. */
  batches: ItemWithExpiry[];
  /** The batch to eat next — what "Verbraucht" acts on. */
  soonest: ItemWithExpiry;
  totalQty: number;
  /** Units in batches that are already urgent, for the "1 läuft ab" hint. */
  urgentQty: number;
  /** True while any batch is newly put away — see JUST_ADDED_MS. */
  justAdded: boolean;
}

/**
 * One tile per product, not per storage row.
 *
 * A row per best-before date is the right data — a jar expiring Saturday
 * genuinely differs from one expiring Sunday — but rendering rows as products
 * fills the grid with the same thing over and over. Grouping happens in the
 * view; the batches stay intact underneath.
 *
 * Memoised, like useBatch: a selector returning a fresh array re-renders
 * forever under zustand's Object.is comparison.
 */
/**
 * How long a new arrival stays visibly new.
 *
 * Derived from addedAt rather than tracked in state: it costs nothing, it is
 * correct after a reload, and it works no matter which device put the item
 * away — which is the point when the phone is doing the scanning.
 */
const JUST_ADDED_MS = 90_000;

export function useItemGroups(): ItemGroup[] {
  const items = useStore((s) => s.items);
  // Re-render on the batch signal so a highlight appears the moment an item
  // lands, and fades on its own without a timer per tile.
  const scanVersion = useStore((s) => s.scanVersion);

  return useMemo(() => {
    const groups = new Map<string, ItemWithExpiry[]>();
    for (const item of items) {
      // Barcode is exact identity; the folded name is the fallback for
      // hand-typed goods. Under-merging is the safe failure here.
      const key = item.barcode ?? foldGerman(item.name);
      const batch = groups.get(key);
      if (batch) batch.push(item);
      else groups.set(key, [item]);
    }

    return [...groups.entries()].map(([key, batches]) => {
      batches.sort((a, b) => {
        // Undated batches sort last: they are never the thing to eat next.
        if (a.bestBefore === b.bestBefore) return 0;
        if (!a.bestBefore) return 1;
        if (!b.bestBefore) return -1;
        return a.bestBefore.localeCompare(b.bestBefore);
      });
      const soonest = batches[0]!;
      return {
        key,
        name: soonest.name,
        imageUrl: batches.find((b) => b.imageUrl)?.imageUrl ?? null,
        batches,
        soonest,
        totalQty: batches.reduce((sum, b) => sum + b.qty, 0),
        urgentQty: batches
          .filter((b) => b.expiry.tone === 'urgent')
          .reduce((sum, b) => sum + b.qty, 0),
        justAdded: batches.some(
          (b) => Date.now() - new Date(b.addedAt).getTime() < JUST_ADDED_MS,
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, scanVersion]);
}

/** The group an item belongs to — for the detail sheet, which opens on a batch. */
export function useGroupOf(itemId: string | null): ItemGroup | null {
  const groups = useItemGroups();
  return useMemo(
    () => (itemId ? (groups.find((g) => g.batches.some((b) => b.id === itemId)) ?? null) : null),
    [groups, itemId],
  );
}
